/**
 * Canonical Currency enum for the billing domain.
 * Uses ISO 4217 currency codes as values.
 * This is the single source of truth for all currency values.
 *
 * DO NOT reference GraphQL or gRPC types directly in domain code.
 * Use the provided mappers to convert between contract types and this enum.
 */
export enum Currency {
  UNKNOWN = 'UNKNOWN',
  USD = 'USD',
  CAD = 'CAD',
  AUD = 'AUD',
  EUR = 'EUR',
  GBP = 'GBP',
  HKD = 'HKD',
  HUF = 'HUF',
  BRL = 'BRL',
  SGD = 'SGD',
  INR = 'INR',
  THB = 'THB',
  AED = 'AED',
  ALL = 'ALL',
  AMD = 'AMD',
  ANG = 'ANG',
  AWG = 'AWG',
  AZN = 'AZN',
  BAM = 'BAM',
  BBD = 'BBD',
  BDT = 'BDT',
  BGN = 'BGN',
  BIF = 'BIF',
  BMD = 'BMD',
  BND = 'BND',
  BSD = 'BSD',
  BWP = 'BWP',
  BYN = 'BYN',
  BZD = 'BZD',
  CDF = 'CDF',
  CHF = 'CHF',
  CNY = 'CNY',
  CZK = 'CZK',
  DKK = 'DKK',
  DOP = 'DOP',
  DZD = 'DZD',
  EGP = 'EGP',
  ETB = 'ETB',
  FJD = 'FJD',
  GEL = 'GEL',
  GIP = 'GIP',
  GMD = 'GMD',
  GYD = 'GYD',
  HTG = 'HTG',
  IDR = 'IDR',
  ILS = 'ILS',
  ISK = 'ISK',
  JMD = 'JMD',
  JPY = 'JPY',
  KES = 'KES',
  KGS = 'KGS',
  KHR = 'KHR',
  KMF = 'KMF',
  KRW = 'KRW',
  KYD = 'KYD',
  KZT = 'KZT',
  LBP = 'LBP',
  LKR = 'LKR',
  LRD = 'LRD',
  LSL = 'LSL',
  MAD = 'MAD',
  MDL = 'MDL',
  MGA = 'MGA',
  MKD = 'MKD',
  MMK = 'MMK',
  MNT = 'MNT',
  MOP = 'MOP',
  MVR = 'MVR',
  MWK = 'MWK',
  MXN = 'MXN',
  MYR = 'MYR',
  MZN = 'MZN',
  NAD = 'NAD',
  NGN = 'NGN',
  NOK = 'NOK',
  NPR = 'NPR',
  NZD = 'NZD',
  PGK = 'PGK',
  PHP = 'PHP',
  PKR = 'PKR',
  PLN = 'PLN',
  QAR = 'QAR',
  RON = 'RON',
  RSD = 'RSD',
  RUB = 'RUB',
  RWF = 'RWF',
  SAR = 'SAR',
  SBD = 'SBD',
  SCR = 'SCR',
  SEK = 'SEK',
  SLE = 'SLE',
  SOS = 'SOS',
  SZL = 'SZL',
  TJS = 'TJS',
  TOP = 'TOP',
  TRY = 'TRY',
  TTD = 'TTD',
  TWD = 'TWD',
  TZS = 'TZS',
  UAH = 'UAH',
  UGX = 'UGX',
  UZS = 'UZS',
  VND = 'VND',
  VUV = 'VUV',
  WST = 'WST',
  XAF = 'XAF',
  XCD = 'XCD',
  XCG = 'XCG',
  YER = 'YER',
  ZAR = 'ZAR',
  ZMW = 'ZMW',
  /** @deprecated Do not use at all */
  EssensysCredits = 'EssensysCredits',
}

// 0 decimal place currencies in Stripe: https://docs.stripe.com/currencies#zero-decimal
// These currencies are not fully supported in Activate
export const No2DecimalCurrencyInStripes = [
  Currency.BIF,
  Currency.JPY,
  Currency.KMF,
  Currency.KRW,
  Currency.MGA,
  Currency.RWF,
  Currency.UGX,
  Currency.VND,
  Currency.VUV,
  Currency.XAF,
  Currency.ISK,
  Currency.TWD,
  Currency.UGX,
];

// Mapping from GraphQL currency string format to domain Currency enum
// Handles backwards compatibility with legacy GraphQL currency strings
const GRAPHQL_STRING_TO_DOMAIN: Record<string, Currency> = {
  CURRENCY_UNKNOWN: Currency.UNKNOWN,
  CURRENCY_USD: Currency.USD,
  CURRENCY_CAD: Currency.CAD,
  CURRENCY_AUD: Currency.AUD,
  CURRENCY_EUR: Currency.EUR,
  CURRENCY_GBP: Currency.GBP,
  CURRENCY_HKD: Currency.HKD,
  CURRENCY_HUF: Currency.HUF,
  CURRENCY_BRL: Currency.BRL,
  CURRENCY_SGD: Currency.SGD,
  CURRENCY_INR: Currency.INR,
  CURRENCY_THB: Currency.THB,
  CURRENCY_AED: Currency.AED,
  CURRENCY_ALL: Currency.ALL,
  CURRENCY_AMD: Currency.AMD,
  CURRENCY_ANG: Currency.ANG,
  CURRENCY_AWG: Currency.AWG,
  CURRENCY_AZN: Currency.AZN,
  CURRENCY_BAM: Currency.BAM,
  CURRENCY_BBD: Currency.BBD,
  CURRENCY_BDT: Currency.BDT,
  CURRENCY_BGN: Currency.BGN,
  CURRENCY_BIF: Currency.BIF,
  CURRENCY_BMD: Currency.BMD,
  CURRENCY_BND: Currency.BND,
  CURRENCY_BSD: Currency.BSD,
  CURRENCY_BWP: Currency.BWP,
  CURRENCY_BYN: Currency.BYN,
  CURRENCY_BZD: Currency.BZD,
  CURRENCY_CDF: Currency.CDF,
  CURRENCY_CHF: Currency.CHF,
  CURRENCY_CNY: Currency.CNY,
  CURRENCY_CZK: Currency.CZK,
  CURRENCY_DKK: Currency.DKK,
  CURRENCY_DOP: Currency.DOP,
  CURRENCY_DZD: Currency.DZD,
  CURRENCY_EGP: Currency.EGP,
  CURRENCY_ETB: Currency.ETB,
  CURRENCY_FJD: Currency.FJD,
  CURRENCY_GEL: Currency.GEL,
  CURRENCY_GIP: Currency.GIP,
  CURRENCY_GMD: Currency.GMD,
  CURRENCY_GYD: Currency.GYD,
  CURRENCY_HTG: Currency.HTG,
  CURRENCY_IDR: Currency.IDR,
  CURRENCY_ILS: Currency.ILS,
  CURRENCY_ISK: Currency.ISK,
  CURRENCY_JMD: Currency.JMD,
  CURRENCY_JPY: Currency.JPY,
  CURRENCY_KES: Currency.KES,
  CURRENCY_KGS: Currency.KGS,
  CURRENCY_KHR: Currency.KHR,
  CURRENCY_KMF: Currency.KMF,
  CURRENCY_KRW: Currency.KRW,
  CURRENCY_KYD: Currency.KYD,
  CURRENCY_KZT: Currency.KZT,
  CURRENCY_LBP: Currency.LBP,
  CURRENCY_LKR: Currency.LKR,
  CURRENCY_LRD: Currency.LRD,
  CURRENCY_LSL: Currency.LSL,
  CURRENCY_MAD: Currency.MAD,
  CURRENCY_MDL: Currency.MDL,
  CURRENCY_MGA: Currency.MGA,
  CURRENCY_MKD: Currency.MKD,
  CURRENCY_MMK: Currency.MMK,
  CURRENCY_MNT: Currency.MNT,
  CURRENCY_MOP: Currency.MOP,
  CURRENCY_MVR: Currency.MVR,
  CURRENCY_MWK: Currency.MWK,
  CURRENCY_MXN: Currency.MXN,
  CURRENCY_MYR: Currency.MYR,
  CURRENCY_MZN: Currency.MZN,
  CURRENCY_NAD: Currency.NAD,
  CURRENCY_NGN: Currency.NGN,
  CURRENCY_NOK: Currency.NOK,
  CURRENCY_NPR: Currency.NPR,
  CURRENCY_NZD: Currency.NZD,
  CURRENCY_PGK: Currency.PGK,
  CURRENCY_PHP: Currency.PHP,
  CURRENCY_PKR: Currency.PKR,
  CURRENCY_PLN: Currency.PLN,
  CURRENCY_QAR: Currency.QAR,
  CURRENCY_RON: Currency.RON,
  CURRENCY_RSD: Currency.RSD,
  CURRENCY_RUB: Currency.RUB,
  CURRENCY_RWF: Currency.RWF,
  CURRENCY_SAR: Currency.SAR,
  CURRENCY_SBD: Currency.SBD,
  CURRENCY_SCR: Currency.SCR,
  CURRENCY_SEK: Currency.SEK,
  CURRENCY_SLE: Currency.SLE,
  CURRENCY_SOS: Currency.SOS,
  CURRENCY_SZL: Currency.SZL,
  CURRENCY_TJS: Currency.TJS,
  CURRENCY_TOP: Currency.TOP,
  CURRENCY_TRY: Currency.TRY,
  CURRENCY_TTD: Currency.TTD,
  CURRENCY_TWD: Currency.TWD,
  CURRENCY_TZS: Currency.TZS,
  CURRENCY_UAH: Currency.UAH,
  CURRENCY_UGX: Currency.UGX,
  CURRENCY_UZS: Currency.UZS,
  CURRENCY_VND: Currency.VND,
  CURRENCY_VUV: Currency.VUV,
  CURRENCY_WST: Currency.WST,
  CURRENCY_XAF: Currency.XAF,
  CURRENCY_XCD: Currency.XCD,
  CURRENCY_XCG: Currency.XCG,
  CURRENCY_YER: Currency.YER,
  CURRENCY_ZAR: Currency.ZAR,
  CURRENCY_ZMW: Currency.ZMW,
};

/**
 * Extract valid currency codes from the Currency enum (excludes UNKNOWN)
 */
type ValidCurrency = Exclude<Currency, Currency.UNKNOWN>;

/**
 * Type alias for valid currency codes (excludes Currency.UNKNOWN).
 *
 * Use this type when you need to ensure a currency is not UNKNOWN.
 * The `isValidCurrency()` function can narrow `Currency` to `CurrencyCode`.
 */
export type CurrencyCode = ValidCurrency;

/**
 * Array of supported payment currencies (excludes EssensysCredits which is deprecated).
 * These are the currencies that can be used for payment processing.
 * This is a subset of CurrencyCode that excludes deprecated currencies.
 */
const SUPPORTED_PAYMENT_CURRENCIES_ARRAY = [
  Currency.USD,
  Currency.CAD,
  Currency.AUD,
  Currency.EUR,
  Currency.GBP,
  Currency.HKD,
  Currency.HUF,
  Currency.BRL,
  Currency.SGD,
  Currency.INR,
  Currency.THB,
  Currency.AED,
  Currency.ALL,
  Currency.AMD,
  Currency.ANG,
  Currency.AWG,
  Currency.AZN,
  Currency.BAM,
  Currency.BBD,
  Currency.BDT,
  Currency.BGN,
  Currency.BIF,
  Currency.BMD,
  Currency.BND,
  Currency.BSD,
  Currency.BWP,
  Currency.BYN,
  Currency.BZD,
  Currency.CDF,
  Currency.CHF,
  Currency.CNY,
  Currency.CZK,
  Currency.DKK,
  Currency.DOP,
  Currency.DZD,
  Currency.EGP,
  Currency.ETB,
  Currency.FJD,
  Currency.GEL,
  Currency.GIP,
  Currency.GMD,
  Currency.GYD,
  Currency.HTG,
  Currency.IDR,
  Currency.ILS,
  Currency.ISK,
  Currency.JMD,
  Currency.JPY,
  Currency.KES,
  Currency.KGS,
  Currency.KHR,
  Currency.KMF,
  Currency.KRW,
  Currency.KYD,
  Currency.KZT,
  Currency.LBP,
  Currency.LKR,
  Currency.LRD,
  Currency.LSL,
  Currency.MAD,
  Currency.MDL,
  Currency.MGA,
  Currency.MKD,
  Currency.MMK,
  Currency.MNT,
  Currency.MOP,
  Currency.MVR,
  Currency.MWK,
  Currency.MXN,
  Currency.MYR,
  Currency.MZN,
  Currency.NAD,
  Currency.NGN,
  Currency.NOK,
  Currency.NPR,
  Currency.NZD,
  Currency.PGK,
  Currency.PHP,
  Currency.PKR,
  Currency.PLN,
  Currency.QAR,
  Currency.RON,
  Currency.RSD,
  Currency.RUB,
  Currency.RWF,
  Currency.SAR,
  Currency.SBD,
  Currency.SCR,
  Currency.SEK,
  Currency.SLE,
  Currency.SOS,
  Currency.SZL,
  Currency.TJS,
  Currency.TOP,
  Currency.TRY,
  Currency.TTD,
  Currency.TWD,
  Currency.TZS,
  Currency.UAH,
  Currency.UGX,
  Currency.UZS,
  Currency.VND,
  Currency.VUV,
  Currency.WST,
  Currency.XAF,
  Currency.XCD,
  Currency.XCG,
  Currency.YER,
  Currency.ZAR,
  Currency.ZMW,
] as const;


/**
 * Supported payment currencies type.
 *
 * Represents currencies that can be used for payment processing. Excludes
 * `Currency.UNKNOWN` and deprecated `Currency.EssensysCredits`. This is a
 * subset of `CurrencyCode` that only includes valid payment currencies.
 */
export type SupportedPaymentCurrency =
  (typeof SUPPORTED_PAYMENT_CURRENCIES_ARRAY)[number];

/**
 * Set of supported payment currencies for efficient lookup.
 *
 * Contains all currencies that can be used for payment processing. Excludes
 * `Currency.UNKNOWN` and deprecated `Currency.EssensysCredits`. Use this set
 * to check if a currency is supported for payments.
 *
 * @example
 * ```ts
 * if (SUPPORTED_PAYMENT_CURRENCIES.has(currency)) {
 *   // Process payment
 * }
 * ```
 */
export const SUPPORTED_PAYMENT_CURRENCIES = new Set<Currency>(
  SUPPORTED_PAYMENT_CURRENCIES_ARRAY
);

/**
 * Parse a currency code string into a Currency enum value.
 *
 * Parses case-insensitive currency codes in multiple formats:
 * - ISO 4217 format (e.g., 'USD', 'usd', 'UsD')
 * - GraphQL format (e.g., 'CURRENCY_USD', 'CURRENCY_CAD') for backwards compatibility
 *
 * Returns `Currency.UNKNOWN` for invalid, missing, or unrecognized values.
 *
 * @param code - Currency code string (ISO 4217 or GraphQL format), undefined, or null
 * @returns Currency enum value, or `Currency.UNKNOWN` if invalid
 *
 * @example
 * ```ts
 * parseCurrency('USD');           // Currency.USD
 * parseCurrency('usd');           // Currency.USD
 * parseCurrency('CAD');           // Currency.CAD
 * parseCurrency('CURRENCY_USD');  // Currency.USD (GraphQL format)
 * parseCurrency('CURRENCY_CAD');  // Currency.CAD (GraphQL format)
 * parseCurrency('XXX');           // Currency.UNKNOWN
 * parseCurrency(undefined);       // Currency.UNKNOWN
 * parseCurrency(null);            // Currency.UNKNOWN
 * ```
 */
export function parseCurrency(code: string | undefined | null): Currency {
  if (!code) {
    return Currency.UNKNOWN;
  }

  const upperCode = code.toUpperCase();

  // First, check if it's a GraphQL-style currency string (CURRENCY_*)
  const graphqlCurrency = GRAPHQL_STRING_TO_DOMAIN[upperCode];

  if (graphqlCurrency !== undefined) {
    return graphqlCurrency;
  }

  // Then check if it's a direct ISO currency code
  // Type guard to check if string is a valid Currency
  const isCurrency = (value: string): value is Currency => {
    return Object.values<string>(Currency).includes(value);
  };

  if (isCurrency(upperCode)) {
    return upperCode;
  }

  return Currency.UNKNOWN;
}

/**
 * Type guard to check if a currency is valid (not UNKNOWN).
 *
 * This function narrows the type from `Currency` to `CurrencyCode`, excluding
 * `Currency.UNKNOWN`. Use this to validate currencies before operations that
 * require valid currency codes.
 *
 * @param currency - Currency enum value to validate
 * @returns True if currency is valid (not UNKNOWN), false otherwise
 *
 * @example
 * ```ts
 * const currency = parseCurrency('USD');
 * if (isValidCurrency(currency)) {
 *   // TypeScript knows currency is CurrencyCode here
 *   processPayment(currency);
 * }
 * ```
 */
export function isValidCurrency(currency: Currency): currency is CurrencyCode {
  return currency !== Currency.UNKNOWN;
}

/**
 * Convert domain Currency enum to ISO 4217 currency code string.
 *
 * Converts a Currency enum value to its ISO currency code string representation.
 * For `Currency.UNKNOWN`, returns 'USD' as a safe fallback.
 *
 * @param currency - Currency enum value to convert
 * @returns ISO 4217 currency code string (e.g., 'USD', 'EUR'), or 'USD' for UNKNOWN
 *
 * @example
 * ```ts
 * currencyToCode(Currency.USD);      // 'USD'
 * currencyToCode(Currency.CAD);      // 'CAD'
 * currencyToCode(Currency.UNKNOWN);   // 'USD' (fallback)
 * ```
 */
export function currencyToCode(currency: Currency): string {
  return currency === Currency.UNKNOWN ? 'USD' : currency;
}

/**
 * Map from Currency enum to SupportedPaymentCurrency.
 * Throws an error for invalid or unsupported currencies instead of silently defaulting.
 *
 * This is the domain-level function for converting Currency to SupportedPaymentCurrency.
 * For server-side code, use this function directly. For client-side code that needs to
 * handle GraphQL types, use the mapper in `domains-billing-web-mappers`.
 *
 * @param currency - Domain Currency enum or undefined
 * @returns SupportedPaymentCurrency (guaranteed to be a valid payment currency)
 * @throws {Error} If currency is UNKNOWN, EssensysCredits, or not in supported payment currencies
 *
 * @example
 * ```ts
 * currencyToPaymentCurrency(Currency.USD);      // Currency.USD
 * currencyToPaymentCurrency(Currency.CAD);      // Currency.CAD
 * currencyToPaymentCurrency(undefined);         // Currency.USD (convenience fallback)
 * currencyToPaymentCurrency(Currency.UNKNOWN);  // throws Error
 * currencyToPaymentCurrency(Currency.EssensysCredits); // throws Error
 * ```
 */
export function currencyToPaymentCurrency(
  currency: Currency | undefined
): SupportedPaymentCurrency {
  // Allow undefined as a convenience for optional values, defaulting to USD
  if (!currency) {
    return Currency.USD;
  }

  // Throw for UNKNOWN - this indicates a parsing/conversion error that should be fixed
  if (currency === Currency.UNKNOWN) {
    throw new Error(
      `Cannot convert UNKNOWN currency to payment currency. This usually indicates a parsing error.`
    );
  }

  // Throw for deprecated EssensysCredits - this should not be used
  if (currency === Currency.EssensysCredits) {
    throw new Error(
      `EssensysCredits is deprecated and cannot be used as a payment currency.`
    );
  }

  // Check if currency is in the set of supported payment currencies
  if (SUPPORTED_PAYMENT_CURRENCIES.has(currency)) {
    return currency;
  }

  // This should never happen with the current enum values (all valid currencies are in SUPPORTED_PAYMENT_CURRENCIES),
  // but this defensive check ensures we fail fast if a new currency is added to the enum without being added to
  // SUPPORTED_PAYMENT_CURRENCIES. This line is intentionally difficult to test without modifying the enum.
  throw new Error(
    `Currency ${currency} is not supported for payments. Supported currencies: ${Array.from(SUPPORTED_PAYMENT_CURRENCIES).join(', ')}`
  );
}


/**
 * Get the precision of a currency.
 *
 * @param currency - Currency enum value to get the precision of
 * @returns The precision of the currency
 */
export function getCurrencyPrecision(currency: Currency): number {
  const opts = new Intl.NumberFormat("en", {
    style: "currency",
    currency,
  }).resolvedOptions();

  return opts.maximumFractionDigits ?? 2;
}
